<template>
  <CustomModal
    :visible="visible"
    :width="'45vw'"
    title="Manage Variables"
    source="voice_agent_dynamic_variable"
    @update:visible="handleClose"
  >
    <div class="dynamic_variable_modal_content">
      <div class="flex flex-col gap-4">
      <div class="flex flex-row items-center justify-between gap-2">
        <CustomInput
          format="text"
          v-model="searchQuery"
          placeholder="Search variables"
          type="primary"
          :icon="imageNames.SEARCH_VARIANT1"
          size="size-4"
          alt="search_icon"
          :onChange="debouncedSearch"
        />

        <a-button class="primary_medium" @click="handleCreateVariable">
          Create Variable
        </a-button>
      </div>

      <div
        v-if="loading"
        class="flex flex-col items-center justify-center gap-2 mt-20"
      >
        <CustomLoader :size="28" />
      </div>

      <div v-else>
        <SimpleTable
          v-if="variables.length > 0"
          :headers="tableColumns"
          :data="variables"
          :loading="loading"
          :identifier-key="'id'"
          :height="'60vh'"
          :show-actions-column="true"
          :actions="variableActions"
        />

        <EmptyScreen
          v-else
          source="voice_agent_dynamic_variables"
          text="No variables found"
          description="Create your first dynamic variable"
          padding="my-20"
        >
          <a-button class="primary_medium mt-4" @click="handleCreateVariable">
            Create Variable
          </a-button>
        </EmptyScreen>
      </div>
      </div>
    </div>
  </CustomModal>

  <CreateOrEditDynamicVariableModal
    :visible="showCreateEditModal"
    :agent-id="agentId"
    :variable="selectedVariable"
    :is-edit-mode="isEditMode"
    @update:visible="showCreateEditModal = $event"
    @saved="handleVariableSaved"
  />

  <ConfirmationModal
    v-model:visible="deleteModalVisible"
    title="Delete Variable"
    :message="`Are you sure you want to delete '${selectedVariable?.variable_name}'?`"
    :loading="isDeleting"
    type="delete"
    confirm-text="Delete"
    loading-text="Deleting..."
    @confirm="handleDeleteConfirm"
    @cancel="deleteModalVisible = false"
  />
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from "vue";
import { debounce } from "lodash";
import { message } from "ant-design-vue";
import CustomModal from "@/src/components/CustomModal.vue";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import CustomLoader from "@/src/components/CustomLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import CreateOrEditDynamicVariableModal from "@/src/components/voice_agents/CreateOrEditDynamicVariableModal.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { dynamicVariableTableHeaders } from "@/src/constants/voiceAgentConstants.js";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  agentId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(["update:visible", "variables-updated"]);

const searchQuery = ref("");
const variables = ref([]);
const loading = ref(false);
const showCreateEditModal = ref(false);
const isEditMode = ref(false);
const selectedVariable = ref(null);
const deleteModalVisible = ref(false);
const isDeleting = ref(false);

const tableColumns = dynamicVariableTableHeaders;

const variableActions = [
  {
    label: "Edit",
    handler: (row) => handleEditVariable(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => handleDeleteVariable(row),
    icon: imageNames.DELETE_GREY,
  },
];

const fetchDynamicVariables = async () => {
  try {
    if (!props.agentId) return;
    
    loading.value = true;
    const response = await VoiceAgentService.getDynamicVariables(
      props.agentId,
      searchQuery.value,
      1,
      30
    );
    
    if (response?.success && response?.variables) {
      variables.value = response.variables;
    }
  } catch (error) {
    console.error("Error fetching dynamic variables:", error);
    variables.value = [];
  } finally {
    loading.value = false;
  }
};

const debouncedSearch = debounce(() => {
  try {
    fetchDynamicVariables();
  } catch (error) {
    console.error("Error in debounced search:", error);
  }
}, 300);

const handleClose = () => {
  emit("update:visible", false);
};

const handleCreateVariable = () => {
  try {
    isEditMode.value = false;
    selectedVariable.value = null;
    showCreateEditModal.value = true;
  } catch (error) {
    console.error("Error opening create variable modal:", error);
  }
};

const handleEditVariable = (record) => {
  try {
    isEditMode.value = true;
    selectedVariable.value = { ...record };
    showCreateEditModal.value = true;
  } catch (error) {
    console.error("Error opening edit variable modal:", error);
  }
};

const handleDeleteVariable = (record) => {
  try {
    selectedVariable.value = { ...record };
    deleteModalVisible.value = true;
  } catch (error) {
    console.error("Error opening delete modal:", error);
  }
};

const handleDeleteConfirm = async () => {
  try {
    if (!selectedVariable.value?.id) {
      message.error("Invalid variable");
      return;
    }

    isDeleting.value = true;
    const response = await VoiceAgentService.deleteDynamicVariable(
      selectedVariable.value.id
    );

    if (response?.success) {
      message.success("Variable deleted successfully");
      deleteModalVisible.value = false;
      selectedVariable.value = null;
      await fetchDynamicVariables();
    } else {
      message.error("Failed to delete variable");
    }
  } catch (error) {
    console.error("Error deleting variable:", error);
    message.error("Failed to delete variable");
  } finally {
    isDeleting.value = false;
  }
};

const handleVariableSaved = async () => {
  try {
    await fetchDynamicVariables();
    emit("variables-updated");
  } catch (error) {
    console.error("Error refreshing variables after save:", error);
  }
};

watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible && props.agentId) {
      fetchDynamicVariables();
    }
  }
);

onBeforeUnmount(() => {
  debouncedSearch.cancel();
});
</script>

<style scoped>
.dynamic_variable_modal_content {
  max-height: 80vh;
  overflow-y: auto;
}
</style>
